import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { change, formValueSelector, reduxForm } from 'redux-form';
import { MatchIdType } from 'components/powerFlowShared/components/modals/filters/criteriaFields';
import { getVariableItems } from 'components/powerFlowShared/components/modals/filters/util/helper';
import { FlowType } from 'fable/flowEditor';
import { Filters, } from '../../components/modals/filters';
import { MatchingOperator, MatchingStrategy } from '../../components/types';
import { SystemVariableId, SystemVariableType, } from '../../helpers/flowVariableHelpers';
import { validate } from './validators/filters';
const FORM_NAME = 'callFlowConditionalForm';
const valueSelect = formValueSelector(FORM_NAME);
export const getDefaultRule = ({ scopeVars, flowType, }) => {
    const [flowVariableItem] = getVariableItems({
        flowType,
        scopeVariables: scopeVars,
    });
    return {
        variable: flowVariableItem.id,
        matching_rule: MatchingOperator.EQUALS,
        match: '',
        variableType: MatchIdType.Input,
    };
};
// e.g. 2021-04-14T22:00:00.000Z[Europe/Copenhagen] or 2023-03-01T00:00:00+01:00[Europe/Madrid]
const dateRegex = /([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}.?.*[zZ]?|[+-][01]\d|2[0-3]:?[0-5]\d?)?\[([^\]]*)]$/;
const getLocalDateObject = (date) => {
    const localOffest = new Date().getTimezoneOffset();
    const localDateObject = moment(date, moment.ISO_8601).add(localOffest, 'minutes');
    const isValidISOString = moment(localDateObject.toISOString(), moment.ISO_8601).isValid();
    return {
        localDateObject,
        isValidISOString,
    };
};
const convertStringToDateObject = (dateString) => {
    const match = dateString ? dateRegex.exec(dateString) : null;
    if (match) {
        const [, date, timezone] = match;
        const { localDateObject, isValidISOString } = getLocalDateObject(date);
        if (isValidISOString) {
            return {
                date,
                localDate: localDateObject.toISOString(),
                timezone,
            };
        }
    }
    return {};
};
const convertDateRangeStringsToDateObject = (dateRange) => {
    if (dateRange) {
        const { start_date, end_date } = dateRange;
        const startDateMatch = start_date ? dateRegex.exec(start_date) : null;
        const endDateMatch = end_date ? dateRegex.exec(end_date) : null;
        if (startDateMatch && endDateMatch) {
            const [, startDate, timezone] = startDateMatch;
            const [, endDate] = endDateMatch;
            const { localDateObject: localStartDateObject, isValidISOString: isStartDateValidISOString, } = getLocalDateObject(startDate);
            const { localDateObject: localEndDateObject, isValidISOString: isEndDateValidISOString, } = getLocalDateObject(endDate);
            if (isStartDateValidISOString && isEndDateValidISOString) {
                return {
                    startDate,
                    localStartDate: localStartDateObject.toISOString(),
                    endDate,
                    localEndDate: localEndDateObject.toISOString(),
                    timezone,
                };
            }
        }
    }
    return {};
};
const getVariableString = (variable) => {
    var _a;
    if (variable.type && variable.id) {
        return `${variable.type}:${variable.id}`;
    }
    return (_a = variable.value) !== null && _a !== void 0 ? _a : '';
};
const getMatchVariable = (variable) => {
    if (variable.id === SystemVariableId.Date) {
        return convertStringToDateObject(variable.value);
    }
    if (variable.id === SystemVariableId.DateRange) {
        return convertDateRangeStringsToDateObject(variable.value);
    }
    if (variable.id === SystemVariableId.SpamProbability) {
        return variable.value * 100; // Convert from 0-1 to the 1-100 range the input expects.
    }
    return getVariableString(variable);
};
const computeVariableType = (variableOrMatch) => {
    switch (variableOrMatch.type) {
        case SystemVariableType.Scope:
        case SystemVariableType.System:
            return MatchIdType.ScopeVariable;
        default:
            switch (variableOrMatch.id) {
                case SystemVariableId.Date:
                    return MatchIdType.Date;
                case SystemVariableId.DateRange:
                    return MatchIdType.DateRange;
                case SystemVariableId.SpamProbability:
                    return MatchIdType.SpamProbabilityVariable;
                default:
                    return MatchIdType.Input;
            }
    }
};
const getVariableType = (rule) => {
    // If match is just an empty base rule (Default rule) compute using variable instead
    if (rule.match.type === SystemVariableType.Input && rule.match.value === '') {
        return computeVariableType(rule.variable);
    }
    return computeVariableType(rule.match);
};
export default (flowType = FlowType.FLOW_TYPE_CALL) => {
    const mapStateToProps = (state, props) => {
        var _a;
        const { node, scopeVars } = props;
        const { payload } = node;
        const initialValues = {
            matching_strategy: (payload === null || payload === void 0 ? void 0 : payload.matching_strategy) || MatchingStrategy.ANY,
            rules: (payload === null || payload === void 0 ? void 0 : payload.rules)
                ? payload.rules.map((rule) => ({
                    match: getMatchVariable(rule.match),
                    matching_rule: rule.matching_rule || MatchingOperator.EQUALS,
                    variable: getVariableString(rule.variable),
                    variableType: getVariableType(rule),
                }))
                : [getDefaultRule({ scopeVars, flowType })],
            description: (_a = payload === null || payload === void 0 ? void 0 : payload.description) !== null && _a !== void 0 ? _a : '',
        };
        return {
            flowType,
            initialValues,
            rules: valueSelect(state, 'rules') || initialValues.rules,
            matching_strategy: valueSelect(state, 'matching_strategy') ||
                initialValues.matching_strategy,
        };
    };
    const mapDispatchToProps = (dispatch) => ({
        changeFormField: (value, field) => {
            dispatch(change(FORM_NAME, field, value));
        },
    });
    return connect(mapStateToProps, mapDispatchToProps)(reduxForm({
        enableReinitialize: true,
        form: FORM_NAME,
        touchOnBlur: false,
        validate,
    })(Filters));
};
